// closed.ts — which experiments and branches the repo page hides by default.
//
// Consolidated and abandoned experiments are finished work. The pane hides
// them, and the graph hides their branches, until the "show closed" switch
// (`?closed=1`) asks for them. Consolidation moves a child's branches onto
// the parent with role 'absorbed', so an absorbed branch is a consolidated
// experiment's branch and is hidden too. The selected experiment is always
// shown, with its own primary branch, even when it is closed.
import type { Experiment } from '../api';

export function isClosed(e: Experiment): boolean {
  return e.status === 'consolidated' || e.status === 'abandoned';
}

/** Experiments the pane lists: open ones, plus the selection. */
export function visibleExperiments(
  experiments: Experiment[],
  showClosed: boolean,
  selected: string,
): Experiment[] {
  if (showClosed) return experiments;
  return experiments.filter((e) => !isClosed(e) || e.slug === selected);
}

/**
 * Branch names the graph hides: every branch of a closed experiment and
 * every absorbed branch, less any branch an open (or the selected)
 * experiment has as its primary. Empty when closed work is shown.
 */
export function hiddenBranches(
  experiments: Experiment[],
  showClosed: boolean,
  selected: string,
): string[] {
  if (showClosed) return [];
  const hidden = new Set<string>();
  const kept = new Set<string>();
  for (const e of experiments) {
    const shown = !isClosed(e) || e.slug === selected;
    for (const b of e.branches ?? []) {
      if (shown && b.role === 'primary') kept.add(b.branch);
      else hidden.add(b.branch);
    }
  }
  return Array.from(hidden).filter((b) => !kept.has(b)).sort();
}
